import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { IMediaSubtaskProps } from '../Content/MediaSubtasksCompleting/MediaSubtaskCompleting'
import { useExecutionUpload } from './useExecutionUpload'
import { useFileCommentsCounts } from './useFileCommentsCounts'
import { useExecutionText } from './useExecutionText'
import { clampMediaIndex } from '../model/media'
import { useTaskOverviewContext } from '../TaskOverviewStackProvider'
import { useResults } from 'components/TasksV2/hooks/useResults'
import { updateResults } from 'redux/actions/tasks_v2'
import { createAIComment, deleteCommentsOfMedia } from 'redux/actions/comments'
import { getMediaCid } from 'utils'
import { callLLMInteraction } from 'redux/actions/promts'
import { parseLLMResponse } from 'utils/parseLLMResponse'

/** Owns the confirmed media and its upload → persist → optional AI analysis lifecycle. */
export const useExecutionMedia = ({
  images,
  videos,
  subtask,
  resultContextId,
  isReadMode,
  mediaType,
  currentMediaIndex = 0,
  onMediaChange,
  retail_id_context_id,
  recurrence_ts,
  tid,
  isReworkLimitReached,
}: IMediaSubtaskProps) => {
  const t = useExecutionText()
  const dispatch: AppDispatch = useDispatch()
  const { setIsSubtaskBusy } = useTaskOverviewContext()
  const { cdn, rid, retailUsersObject } = useSelector((s: RootState) => s.config.config)
  const taskSubtask = useSelector((s: RootState) =>
    s.tasks_v2.subtasks[tid]?.find((item) => item.st_id === subtask.st_id)
  )
  const result = useResults({ subtask: { st_id: subtask.st_id, tid }, resultContextId, recurrence_ts })
  const [media, setMedia] = useState(() => [...images, ...videos])
  const [analysisLoading, setAnalysisLoading] = useState(false)
  const [analysisError, setAnalysisError] = useState(false)
  const [mutationLoading, setMutationLoading] = useState(false)
  const [saveError, setSaveError] = useState(false)

  const mediaRef = useRef(media)
  const analysisLock = useRef(false)
  const mutationLock = useRef(false)
  const replacePending = useRef(false)
  const mounted = useRef(true)
  const isImage = mediaType === keys.SUBTASK_TYPES.IMAGE_SUBTASK
  const oid = `${subtask.st_id}_${resultContextId}${recurrence_ts ? '_' + recurrence_ts : ''}`
  const counts = useFileCommentsCounts(oid, media)
  const index = clampMediaIndex(currentMediaIndex, media.length)
  const currentUrl = media[index]

  const save = async (urls: string[], status: number, reservations = result?.reservations) => {
    const ok = await dispatch(
      updateResults({
        tid,
        st_id: subtask.st_id,
        resultContextId,
        type: mediaType,
        images: isImage ? urls : [],
        videos: isImage ? [] : urls,
        status,
        recurrence_ts,
        rating: result?.rating,
        reservations,
        optimistic: false,
      })
    )
    if (!ok) throw new Error(t.saveFailed)
  }

  const removeComments = (urls: string[]) => {
    const hashes = urls.map((url) => getMediaCid({ url, cdn, rid })).filter(Boolean) as string[]
    if (hashes.length)
      dispatch(
        deleteCommentsOfMedia(
          hashes.map((hash) => `${oid}-${hash}`),
          hashes,
          hashes.map(() => oid)
        )
      )
  }

  const onUploaded = async (url: string) => {
    const old = mediaRef.current
    const next = [...(replacePending.current ? [] : old), url]
    await save(
      next,
      keys.TASK_STATUSES.DONE.value,
      replacePending.current ? { reasons: [], score_deduction: 0 } : result?.reservations
    )
    if (!mounted.current) return
    if (replacePending.current) removeComments(old)
    replacePending.current = false
    mediaRef.current = next
    setMedia(next)
    onMediaChange?.(next.length - 1)
  }
  const upload = useExecutionUpload(onUploaded)
  const navigationDisabled = upload.busy || analysisLoading || mutationLoading
  const interactionBlocked = isReadMode || !!isReworkLimitReached || navigationDisabled

  const setSelected = (next: number) => {
    if (navigationDisabled || analysisLock.current || mutationLock.current) return
    onMediaChange?.(clampMediaIndex(next, media.length))
  }

  const analyze = async () => {
    const urls = mediaRef.current
    if (
      !mounted.current ||
      !urls.length ||
      isReadMode ||
      isReworkLimitReached ||
      !subtask.is_ai_enhanced ||
      analysisLock.current ||
      mutationLock.current
    )
      return
    analysisLock.current = true
    setAnalysisLoading(true)
    setAnalysisError(false)
    try {
      const response = await dispatch(
        callLLMInteraction({
          images: urls,
          reference_images: subtask.reference_images ?? [],
          message_id: subtask.message_id ?? '',
          prompt_id: subtask.prompt_id ?? '',
          message: '',
        })
      )
      if (!mounted.current) return
      if (!response) throw new Error('No AI response')
      const parsed = parseLLMResponse(response)
      await save(urls, keys.TASK_STATUSES.REVIEWED_BY_AI.value, parsed.reservations)
      if (!mounted.current) return
      const parentCid = getMediaCid({ url: urls[urls.length - 1], cdn, rid })
      if (parentCid)
        await dispatch(
          createAIComment({
            oid,
            message: parsed.feedbackText,
            parentCid,
            rasId: retail_id_context_id,
            objectType: keys.COMMENT_OBJECT_TYPE.COMMENT_ON_IMAGE,
            taskId: tid,
            resultContextId,
            resultLevel: Object.keys(retailUsersObject ?? {}).includes(resultContextId) ? 'user' : 'group',
            keyForSave: `${oid}-${parentCid}`,
            recurrenceTs: recurrence_ts,
            is_ai: true,
          })
        )
    } catch {
      if (mounted.current) setAnalysisError(true)
    } finally {
      analysisLock.current = false
      if (mounted.current) setAnalysisLoading(false)
    }
  }

  const addFiles = async (files: File[]) => {
    if (interactionBlocked || analysisLock.current || mutationLock.current) return
    const accepted = files.filter((file) => file.type.startsWith(isImage ? 'image/' : 'video/'))
    if (!accepted.length) return
    const before = mediaRef.current
    replacePending.current = !!subtask.is_ai_enhanced && before.length > 0
    await upload.add(accepted)
    if (mediaRef.current !== before) await analyze()
  }

  const retry = async () => {
    if (interactionBlocked || analysisLock.current || mutationLock.current) return
    const before = mediaRef.current
    await upload.retry()
    if (mediaRef.current !== before) await analyze()
  }

  const deleteCurrent = async () => {
    if (interactionBlocked || analysisLock.current || mutationLock.current || !currentUrl) return
    mutationLock.current = true
    setMutationLoading(true)
    setSaveError(false)
    try {
      const next = mediaRef.current.filter((_, i) => i !== index)
      await save(next, next.length ? keys.TASK_STATUSES.DONE.value : keys.TASK_STATUSES.INPROGRESS.value, {
        reasons: [],
        score_deduction: 0,
      })
      if (!mounted.current) return
      removeComments([currentUrl])
      mediaRef.current = next
      setMedia(next)
    } catch {
      if (mounted.current) setSaveError(true)
    } finally {
      mutationLock.current = false
      if (mounted.current) setMutationLoading(false)
    }
  }
  // The backend stores one score for the subtask and attaches its AI comment to the last file.
  // Never repeat that score as if every file had been analysed separately.
  const score =
    subtask.is_ai_enhanced &&
    result?.status === keys.TASK_STATUSES.REVIEWED_BY_AI.value &&
    typeof result.reservations?.score_deduction === 'number' &&
    Number.isFinite(result.reservations.score_deduction) &&
    index === media.length - 1
      ? 100 - result.reservations.score_deduction
      : undefined
  const canAdd = !isReadMode && !isReworkLimitReached

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  useEffect(() => {
    if (index !== currentMediaIndex) onMediaChange?.(index)
  }, [index, currentMediaIndex, onMediaChange])

  useEffect(() => {
    setIsSubtaskBusy(upload.busy || analysisLoading || mutationLoading)
    return () => setIsSubtaskBusy(false)
  }, [upload.busy, analysisLoading, mutationLoading, setIsSubtaskBusy])

  useEffect(() => {
    if (upload.busy || mutationLoading || analysisLoading) return
    const confirmed = [...images, ...videos]
    if (confirmed.join('\n') === mediaRef.current.join('\n')) return
    mediaRef.current = confirmed
    setMedia(confirmed)
  }, [images, videos, upload.busy, mutationLoading, analysisLoading])

  return {
    taskSubtask,
    result,
    media,
    index,
    currentUrl,
    counts,
    setSelected,
    upload,
    navigationDisabled,
    interactionBlocked,
    analysisLoading,
    analysisError,
    saveError,
    analyze,
    addFiles,
    retry,
    deleteCurrent,
    score,
    canAdd,
    isImage,
  }
}
